import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const {FiGrid,FiCheckCircle,FiArrowRight,FiLayers,FiSettings,FiShield}=FiIcons;

function Bodenbelag() {
  const floorTypes=[
    {
      title: 'Laminat & Parkett',
      description: 'Hochwertige Holzböden für natürliche Wärme und Eleganz.',
      features: ['Natürliche Optik','Langlebig','Pflegeleicht','Verschiedene Holzarten'],
      image: 'https://mx.ausbau.nrw/img/boden/laminat-parkett.jpg'
    },
    {
      title: 'Vinyl & PVC-Böden',
      description: 'Moderne und pflegeleichte Böden in vielen Designs.',
      features: ['Wasserresistent','Strapazierfähig','Designvielfalt','Schnelle Verlegung'],
      image: 'https://mx.ausbau.nrw/img/boden/pvc_vinyl.jpg'
    },
    {
      title: 'Fliesen & Naturstein',
      description: 'Robuste und elegante Lösungen für Bad,Küche und Wohnbereich.',
      features: ['Feuchtigkeitsresistent','Hygienisch','Temperaturbeständig','Edle Optik'],
      image: 'https://mx.ausbau.nrw/img/boden/fliesen_naturstein.jpg'
    },
    {
      title: 'Teppichböden',
      description: 'Komfortable und warme Böden für Wohn- und Schlafräume.',
      features: ['Wärmedämmend','Schalldämpfend','Komfortabel','Große Farbauswahl'],
      image: 'https://mx.ausbau.nrw/img/boden/teppich.jpg'
    }
  ];

  const services=[
    {icon: FiLayers,title: 'Untergrundvorbereitung',description: 'Professionelle Vorbereitung für optimale Verlegung und Haltbarkeit.'},
    {icon: FiSettings,title: 'Fachgerechte Verlegung',description: 'Präzise Installation durch erfahrene Bodenleger-Fachkräfte.'},
    {icon: FiShield,title: 'Qualitätsmaterialien',description: 'Hochwertige Bodenbeläge renommierter Hersteller für lange Freude.'}
  ];

  const advantages=[
    'Kostenlose Beratung und Materialauswahl',
    'Präzise Aufmaß und Kostenvoranschlag',
    'Professionelle Untergrundprüfung',
    'Fachgerechte Verlegung aller Bodenarten',
    'Randbereiche und Übergänge perfekt ausgeführt',
    'Nachbehandlung und Pflege-Beratung'
  ];

  const roomTypes=[
    {room: 'Wohnzimmer',recommendation: 'Parkett,Laminat,hochwertige Vinylböden'},
    {room: 'Küche',recommendation: 'Fliesen,Vinylböden,pflegeleichte Materialien'},
    {room: 'Badezimmer',recommendation: 'Feuchtigkeitsresistente Fliesen und Vinylböden'},
    {room: 'Schlafzimmer',recommendation: 'Parkett,Laminat,warme Teppichböden'},
    {room: 'Flur/Eingang',recommendation: 'Strapazierfähige Fliesen und Vinylböden'},
    {room: 'Arbeitszimmer',recommendation: 'Laminat,Parkett,schalldämpfende Böden'}
  ];

  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
          className="flex items-center space-x-2 text-sm text-gray-600 mb-8"
        >
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900">Bodenbelagsarbeiten</span>
        </motion.nav>

        {/* Header */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.1}}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiGrid} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Bodenbelagsarbeiten
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professionelle Verlegung verschiedener Bodenbeläge von Laminat bis Fliesen. Schaffen Sie die perfekte Basis für Ihr Zuhause mit hochwertigen Materialien.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.8,delay: 0.2}}
          className="mb-16"
        >
          <img
            src="https://mx.ausbau.nrw/img/Bodenbelagsarbeiten_xl.jpg"
            alt="Bodenbelagsarbeiten"
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Services Overview */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.3}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unsere Leistungen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service,index)=> (
              <motion.div
                key={service.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.4 + index * 0.1}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Floor Types */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.5}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Bodenbelag-Arten im Detail
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {floorTypes.map((floor,index)=> (
              <motion.div
                key={floor.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.6 + index * 0.1}}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <img
                  src={floor.image}
                  alt={floor.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {floor.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {floor.description}
                  </p>
                  <ul className="space-y-2">
                    {floor.features.map((feature,featureIndex)=> (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-mk-teal-500" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Room Recommendations */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.7}}
          className="bg-gray-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Empfehlungen nach Räumen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roomTypes.map((item,index)=> (
              <motion.div
                key={item.room}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 0.8 + index * 0.1}}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {item.room}
                </h3>
                <p className="text-gray-600 text-sm">
                  {item.recommendation}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advantages Section */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.8}}
          className="bg-mk-teal-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Ihre Vorteile bei MK-AUSBAU
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((advantage,index)=> (
              <motion.div
                key={advantage}
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                transition={{duration: 0.6,delay: 0.9 + index * 0.1}}
                className="flex items-center space-x-3"
              >
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-mk-teal-600" />
                <span className="text-gray-700 font-medium">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 0.9}}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unser Verlegeprozess
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              {step: '1',title: 'Beratung',description: 'Materialauswahl und Raumplanung'},
              {step: '2',title: 'Aufmaß',description: 'Präzise Vermessung der Räume'},
              {step: '3',title: 'Vorbereitung',description: 'Untergrundprüfung und -vorbereitung'},
              {step: '4',title: 'Verlegung',description: 'Fachgerechte Installation des Belags'},
              {step: '5',title: 'Abschluss',description: 'Reinigung und Übergabe'}
            ].map((item,index)=> (
              <motion.div
                key={item.step}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: 1.0 + index * 0.1}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6,delay: 1.1}}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Ihr neuer Traumboden wartet!
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Lassen Sie sich von unserer Auswahl inspirieren und finden Sie den perfekten Bodenbelag für Ihr Zuhause. Kostenlose Beratung inklusive!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Materialberatung buchen</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Services Navigation Footer */}
      <ServicesNavigationFooter />

      {/* Google Maps Section */}
      <GoogleMaps />
    </div>
  );
}

export default Bodenbelag;